import { Language } from "@/types/language.types";

class RouteUrls {
  public baseUrl = "/";
  public loginBase = "/login";
  public profileBase = "/profile";
  public dashboardBase = "/dashboard";
  public baseWorkflow = "/workflow";
  public baseCustomer = "/customers/accounts";
  public baseUsersApp = "/customers/userApp";
  public baseSalesProduct = "/salesProduct";
  public baseUserBackoffice = "/usersBackoffice";
  public baseProductPricing = "/productPricing";
  public baseSalesProductPrices = "/salesProductPrice";
  public basePlatform = "/platform";
  public basePrompt = "/prompt";
  public baseCoverageArea = "/coverage-area";
  public baseChangelog = "/changelog";
  public baseSubscriptions = "/subscriptions";

  private redirect = (path: string): string => `/?redirect=${path}`;

  private create = (base: string, lang: Language, suffix: string = ""): string =>
    `${base}${suffix ? `/${suffix}` : ""}/${lang}`;

  private update = (base: string, id: number, lang: Language): string =>
    `${base}/update/${id}/${lang}`;

  // Login Routes
  public login = (lang: Language): string => this.create(this.loginBase, lang);
  public loginRedirect = (redirect: string, lang: Language): string =>
    `${this.login(lang)}${this.redirect(redirect)}`;

  // Sales Dashboard Routes
  public salesDashboard = (lang: Language): string =>
    this.create(this.dashboardBase, lang, "sales");
  public createSalesDashboard = (lang: Language): string =>
    this.create(this.dashboardBase, lang, "sales/create");
  public updateSalesDashboard = (lang: Language, dashboardId: number): string =>
    this.update(this.dashboardBase, dashboardId, lang);

  // Service Dashboard Routes
  public serviceDashboard = (lang: Language): string =>
    this.create(this.dashboardBase, lang, "service");
  public createServiceDashboard = (lang: Language): string =>
    this.create(this.dashboardBase, lang, "service/create");
  public updateServiceDashboard = (lang: Language, dashboardId: number): string =>
    this.update(this.dashboardBase, dashboardId, lang);

  // Profile Routes
  public profile = (lang: Language): string => this.create(this.profileBase, lang);
  public permissions = (lang: Language): string =>
    this.create(this.profileBase, lang, "permissions");
  public changePassword = (lang: Language): string =>
    this.create(this.profileBase, lang, "change-password");

  // Sales Product Routes
  public salesProducts = (lang: Language): string => this.create(this.baseSalesProduct, lang);
  public createSalesProduct = (lang: Language): string =>
    this.create(this.baseSalesProduct, lang, "create");
  public updateSalesProduct = (lang: Language, productId: number): string =>
    this.update(this.baseSalesProduct, productId, lang);

  // Workflow Routes
  public workflow = (lang: Language): string => this.create(this.baseWorkflow, lang);
  public createWorkflow = (lang: Language): string =>
    this.create(this.baseWorkflow, lang, "create");
  public updateWorkflow = (lang: Language, workflowId: number): string =>
    this.update(this.baseWorkflow, workflowId, lang);
  public viewWorkflow = (lang: Language, workflowId: number): string =>
    `${this.baseWorkflow}/flow/${workflowId}/${lang}`;

  // Customer Routes
  public customer = (lang: Language): string => this.create(this.baseCustomer, lang);
  public createCustomer = (lang: Language): string =>
    this.create(this.baseCustomer, lang, "create");
  public updateCustomer = (lang: Language, customerId: number): string =>
    this.update(this.baseCustomer, customerId, lang);

  // User App Routes
  public userApp = (lang: Language): string => this.create(this.baseUsersApp, lang);
  public createCustomerUserApp = (lang: Language): string =>
    this.create(this.baseUsersApp, lang, "create");
  public updateCustomerUserApp = (lang: Language, userId: number): string =>
    this.update(this.baseUsersApp, userId, lang);
  public filterCustomerUsersAppByCustomer = (lang: Language, customerId: number): string =>
    `${this.baseUsersApp}/filterByCustomer/${customerId}/${lang}`;

  // Sales Product Price Routes
  public createSalesProductPrice = (lang: Language, productId: number): string =>
    `${this.baseSalesProductPrices}/create/${productId}/${lang}`;
  public updateSalesProductPrice = (lang: Language, productId: number): string =>
    this.update(this.baseSalesProductPrices, productId, lang);
  public filterSalesProductPricesByProductId = (lang: Language, productId: number): string =>
    `${this.baseSalesProductPrices}/filterByProductId/${productId}/${lang}`;

  // User Backoffice Routes
  public usersBackoffice = (lang: Language): string => this.create(this.baseUserBackoffice, lang);
  public createUserBackoffice = (lang: Language): string =>
    this.create(this.baseUserBackoffice, lang, "create");
  public updateUserBackoffice = (lang: Language, userId: number): string =>
    this.update(this.baseUserBackoffice, userId, lang);

  // Platform Routes
  public platform = (lang: Language): string => this.create(this.basePlatform, lang);
  public createPlatform = (lang: Language): string =>
    this.create(this.basePlatform, lang, "create");
  public updatePlatform = (lang: Language, platformId: number): string =>
    this.update(this.basePlatform, platformId, lang);

  // Prompt Routes
  public prompts = (lang: Language): string => this.create(this.basePrompt, lang);
  public createPrompt = (lang: Language): string => this.create(this.basePrompt, lang, "create");
  public updatePrompt = (lang: Language, promptId: number): string =>
    this.update(this.basePrompt, promptId, lang);

  // --------- COVERAGE AREA ROUTES ---------

  public coverageAreas = (lang: Language): string => this.create(this.baseCoverageArea, lang);
  public createCoverageArea = (lang: Language): string =>
    this.create(this.baseCoverageArea, lang, "create");
  public updateCoverageArea = (lang: Language, coverageAreaId: number): string =>
    this.update(this.baseCoverageArea, coverageAreaId, lang);

  // --------- CHANGELOG ROUTES ---------

  public changelog = (lang: Language): string => this.create(this.baseChangelog, lang);
  public changelogLogs = (lang: Language): string => this.create(this.baseChangelog, lang, "logs");
  public changelogTables = (lang: Language): string =>
    this.create(this.baseChangelog, lang, "tables");
  public createChangelogTable = (lang: Language): string =>
    this.create(this.baseChangelog, lang, "tables/create");
  public updateChangelogTable = (lang: Language, tableId: number): string =>
    `${this.baseChangelog}/tables/update/${tableId}/${lang}`;

  // Subscriptions (Assinaturas) Routes
  public subscriptions = (lang: Language): string => this.create(this.baseSubscriptions, lang);
  public createSubscription = (lang: Language): string => this.create(this.baseSubscriptions, lang, "create");
  public updateSubscription = (lang: Language, subscriptionId: number): string =>
    this.update(this.baseSubscriptions, subscriptionId, lang);
}

const ROUTE_URLS = new RouteUrls();
export default ROUTE_URLS;
export const UNPROTECTED_ROUTE = ROUTE_URLS.loginBase;
